"use client";
import { FormError, FormGroup, Input, Label } from "cy-fe";
import { Controller, UseFormReturn } from "react-hook-form";
import { MonthInfo, months } from "cy-util";
import { ChevronDown } from "lucide-react";
import { ComboBox, renderSelectOptions } from "cy-ui/components/Combobox";
import { PersonalInfoInputs } from "./models";
import { cn } from "cy-fe/src/utils/cn";
import { useEffect } from "react";
import { differenceInYears } from "date-fns";

export function DateOfBirth({
  form,
}: {
  form: UseFormReturn<PersonalInfoInputs, any, PersonalInfoInputs>;
}) {
  const {
    formState: {
      errors: { dateOfBirth: dateOfBirthErrors },
    },
  } = form;
  const day = form.watch("dateOfBirth.day") as number | undefined;
  const month = form.watch("dateOfBirth.month") as number | undefined;
  const year = form.watch("dateOfBirth.year") as number | undefined;
  const setError = form.setError;
  const clearErrors = form.clearErrors;
  useEffect(() => {
    const yearStr = year?.toString().padStart(4, "0");
    const monthStr = month?.toString().padStart(2, "0");
    const dayStr = day?.toString().padStart(2, "0");
    const dateStr = `${yearStr}-${monthStr}-${dayStr}`;
    const date = new Date(dateStr);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition --  it can be
    const parsedDateStr = date.toJSON()?.slice(0, 10);
    let hasError = isNaN(date.getDate()) || parsedDateStr !== dateStr;
    if (!hasError) {
      const yearsDiff = differenceInYears(new Date(), date);
      hasError = yearsDiff < 18 || yearsDiff > 100;
    }
    if (hasError) {
      setError("dateOfBirth", {
        message: "Invalid date of birth",
        type: "dateOfBirth",
      });
    } else {
      clearErrors("dateOfBirth");
    }
  }, [day, month, year, clearErrors, setError]);
  const showError = !!dateOfBirthErrors && form.formState.isSubmitted;
  return (
    <div data-testid="date-of-birth-input">
      <Label>Date of birth</Label>
      <div className="flex gap-4">
        <FormGroup
          controller={{ control: form.control, name: "dateOfBirth.day" }}
        >
          <Input
            aria-label="dob day"
            className="w-16"
            placeholder="DD"
            type="tel"
            error={showError}
            vSize="lg"
            data-testid="dob-day-input"
            {...form.register("dateOfBirth.day", {
              valueAsNumber: true,
            })}
          />
        </FormGroup>
        <Controller
          control={form.control}
          name="dateOfBirth.month"
          render={({ field }) => (
            <ComboBox<MonthInfo>
              label="dob month"
              buttonChildren={(val) => (
                <>
                  {val?.name || "Month"}
                  <ChevronDown className="absolute right-2" />
                </>
              )}
              error={showError}
              buttonClassName={(val) => {
                return cn("flex-grow", !val && "text-gray-400");
              }}
              getOptions={(query) => {
                query = query.trim().toLowerCase();
                return months.filter(
                  (x) => !query || x.name.toLowerCase().includes(query),
                );
              }}
              maxHeight={16 * 30}
              onChange={(x) => field.onChange(x?.number ?? null)}
              renderItems={(opts) =>
                renderSelectOptions(
                  opts.map((x) => ({ value: x, key: x.number, label: x.name })),
                )
              }
              value={months.find((x) => x.number === field.value) || null}
              data-testid="dob-month-select"
            />
          )}
        />
        <FormGroup
          controller={{ control: form.control, name: "dateOfBirth.year" }}
        >
          <Input
            aria-label="dob year"
            className="w-20"
            placeholder="YYYY"
            type="tel"
            error={showError}
            vSize="lg"
            data-testid="dob-year-input"
            {...form.register("dateOfBirth.year", {
              valueAsNumber: true,
            })}
          />
        </FormGroup>
      </div>
      <FormError>{showError && dateOfBirthErrors?.message}</FormError>
    </div>
  );
}
